/**
 * Payment Gateway Settings Component
 * Admin UI for configuring payment gateways (Stripe/PayMongo)
 */

import React, { useState, useEffect } from 'react';
import {
  listPaymentGateways,
  getPaymentGatewayConfig,
  setActivePaymentGateway,
  saveGatewayApiKeys,
  testPaymentGateway,
} from '../services/gatewayService';
import './PaymentGatewaySettings.css';

const PaymentGatewaySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);
  const [gateways, setGateways] = useState([]);
  const [config, setConfig] = useState(null);
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [showKeyForm, setShowKeyForm] = useState(null);
  const [keyForm, setKeyForm] = useState({ secretKey: '', publicKey: '' });
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [gatewaysResult, configResult] = await Promise.all([
        listPaymentGateways(),
        getPaymentGatewayConfig(),
      ]);
      setGateways(gatewaysResult.gateways || []);
      setConfig(configResult);
      setSelectedGateway(configResult.currentGateway);
    } catch (error) {
      console.error('Failed to load gateway data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectGateway = async (gatewayId) => {
    if (gatewayId === config?.currentGateway) return;

    setSaving(true);
    try {
      await setActivePaymentGateway(gatewayId);
      setSelectedGateway(gatewayId);
      await loadData(); // Refresh config
      alert(`Switched to ${gatewayId.charAt(0).toUpperCase() + gatewayId.slice(1)}`);
    } catch (error) {
      alert('Failed to switch gateway: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveKeys = async (gatewayId) => {
    if (!keyForm.secretKey) {
      alert('Secret key is required');
      return;
    }

    setSaving(true);
    try {
      await saveGatewayApiKeys(gatewayId, keyForm.secretKey, keyForm.publicKey);
      setShowKeyForm(null);
      setKeyForm({ secretKey: '', publicKey: '' });
      await loadData();
      alert('API keys saved successfully');
    } catch (error) {
      alert('Failed to save keys: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (gatewayId) => {
    setTesting(gatewayId);
    setTestResult(null);
    try {
      const result = await testPaymentGateway(gatewayId);
      setTestResult({ gateway: gatewayId, ...result });
    } catch (error) {
      setTestResult({ gateway: gatewayId, success: false, error: error.message });
    } finally {
      setTesting(null);
    }
  };

  if (loading) {
    return (
      <div className="gateway-settings loading">
        <div className="spinner"></div>
        <p>Loading payment settings...</p>
      </div>
    );
  }

  return (
    <div className="gateway-settings">
      <div className="settings-header">
        <h2>Payment Gateway</h2>
        <p className="settings-description">
          Choose your payment processor. Switch anytime - your saved payment methods will remain available.
        </p>
      </div>

      <div className="gateway-list">
        {gateways.map(gateway => {
          const isActive = config?.currentGateway === gateway.id;
          const isConfigured = config?.savedGateways?.includes(gateway.id);

          return (
            <div
              key={gateway.id}
              className={`gateway-card ${isActive ? 'active' : ''} ${isConfigured ? 'configured' : ''}`}
            >
              <div className="gateway-header">
                <div className="gateway-info">
                  <h3>{gateway.name}</h3>
                  {isActive && <span className="active-badge">Active</span>}
                  {isConfigured && !isActive && <span className="configured-badge">Configured</span>}
                </div>
                <div className="gateway-actions">
                  {!isActive && isConfigured && (
                    <button
                      className="btn btn-primary"
                      onClick={() => handleSelectGateway(gateway.id)}
                      disabled={saving}
                    >
                      {saving ? 'Switching...' : 'Use This'}
                    </button>
                  )}
                  {isActive && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleTestConnection(gateway.id)}
                      disabled={testing === gateway.id}
                    >
                      {testing === gateway.id ? 'Testing...' : 'Test Connection'}
                    </button>
                  )}
                </div>
              </div>

              <p className="gateway-description">{gateway.description}</p>

              <div className="gateway-details">
                <div className="detail-item">
                  <span className="label">Currencies:</span>
                  <span className="value">{gateway.currencies.join(', ')}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Features:</span>
                  <span className="value">{gateway.features.join(', ')}</span>
                </div>
                <div className="detail-item">
                  <span className="label">Regions:</span>
                  <span className="value">{gateway.regions.join(', ')}</span>
                </div>
              </div>

              {/* Test Result */}
              {testResult && testResult.gateway === gateway.id && (
                <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                  {testResult.success ? (
                    <span>Connection successful</span>
                  ) : (
                    <span>Connection failed: {testResult.error}</span>
                  )}
                </div>
              )}

              {/* API Key Configuration */}
              {showKeyForm === gateway.id ? (
                <div className="key-form">
                  <h4>Configure {gateway.name} API Keys</h4>
                  <div className="form-group">
                    <label>Secret Key</label>
                    <input
                      type="password"
                      className="input"
                      placeholder={gateway.id === 'stripe' ? 'sk_live_...' : 'sk_live_...'}
                      value={keyForm.secretKey}
                      onChange={(e) => setKeyForm({ ...keyForm, secretKey: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Public Key</label>
                    <input
                      type="text"
                      className="input"
                      placeholder={gateway.id === 'stripe' ? 'pk_live_...' : 'pk_live_...'}
                      value={keyForm.publicKey}
                      onChange={(e) => setKeyForm({ ...keyForm, publicKey: e.target.value })}
                    />
                  </div>
                  <div className="form-actions">
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowKeyForm(null);
                        setKeyForm({ secretKey: '', publicKey: '' });
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleSaveKeys(gateway.id)}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Keys'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="btn-link configure-btn"
                  onClick={() => setShowKeyForm(gateway.id)}
                >
                  {isConfigured ? 'Update API Keys' : 'Configure API Keys'}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <div className="gateway-help">
        <h3>Getting API Keys</h3>
        <div className="help-cards">
          <div className="help-card">
            <h4>Stripe</h4>
            <ol>
              <li>Go to <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer">Stripe Dashboard</a></li>
              <li>Copy your Secret key (starts with sk_)</li>
              <li>Copy your Publishable key (starts with pk_)</li>
            </ol>
          </div>
          <div className="help-card">
            <h4>PayMongo</h4>
            <ol>
              <li>Go to <a href="https://dashboard.paymongo.com/developers" target="_blank" rel="noopener noreferrer">PayMongo Dashboard</a></li>
              <li>Navigate to Developers {">"} API Keys</li>
              <li>Copy your Secret key and Public key</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentGatewaySettings;
